import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Download, Loader2 } from 'lucide-react';
import { apiGet, createHeaders, getJWT, API_BASE_URL } from '../../auth';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = API_BASE_URL;

export const SurveyorReportEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [assignment, setAssignment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [assessmentText, setAssessmentText] = useState(t('surveys.portal.editor.assessment_default'));
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignmentDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  const fetchAssignmentDetails = async () => {
    if (!getJWT()) return; // Stop if no token
    try {
      const assignments = await apiGet<any[]>('api/surveyor/assignments');
      const current = assignments.find(a => a.id === id);
      if (!current) throw new Error(t('surveys.portal.editor.not_found'));
      
      // Fetch photos via the guest_token
      const infoRes = await fetch(`${API_URL}/api/surveys/guest/${current.report_history.guest_token}`);
      const info = await infoRes.json();
      current.photos = info.photo_urls || [];
      
      setAssignment(current);
    } catch (err: any) {
      alert(err.message);
      navigate('/surveyor/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const headers = createHeaders(getJWT());
      headers.set('Content-Type', 'application/json');
      const response = await fetch(`${API_URL}/api/surveyor/generate_pdf`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          assignment_id: id,
          assessment_text: assessmentText,
          selected_photos: assignment.photos
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || t('surveys.portal.editor.generate_error'));
      
      setGeneratedUrl(result.download_url);
    } catch (err: any) {
      alert(t('surveys.portal.editor.generate_error') + ': ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading || !assignment) return <div className="p-10 text-center text-slate-500 font-medium">{t('surveys.modal.loading')}</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
        
        {/* Sol Panel: Müşteri Verileri & Kayıtlar */}
        <div className="w-full lg:w-1/3 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h2 className="text-xl font-bold border-b border-slate-100 pb-3 mb-4 text-slate-800">{t('surveys.portal.editor.details_title')}</h2>
          
          <div className="space-y-6">
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('surveys.portal.editor.project_name')}</p>
              <p className="font-semibold text-slate-900">{assignment.report_history.project_description || '-'}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{t('surveys.portal.editor.weight')}</p>
              <p className="font-semibold text-slate-900">{assignment.report_history.cargo_weight_mt} MT</p>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{t('surveys.portal.editor.photos_count')} ({assignment.photos?.length || 0})</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {assignment.photos.map((url: string, idx: number) => (
                   <img key={idx} src={url} alt={`Cargo ${idx+1}`} className="w-full h-24 object-cover rounded-lg border border-slate-100 shadow-sm" />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sağ Panel: Rapor Tasarımı veya Başarı Durumu */}
        <div className="w-full lg:w-2/3 bg-white p-8 rounded-xl shadow-xl border-t-4 border-indigo-600">
          
          {generatedUrl ? (
            <div className="flex flex-col items-center justify-center py-12 text-center h-full animate-in fade-in zoom-in duration-500">
              <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6 shadow-inner">
                <CheckCircle className="w-12 h-12" />
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">{t('surveys.portal.editor.success_title')}</h2>
              <p className="text-slate-600 mb-10 max-w-md leading-relaxed">
                {t('surveys.portal.editor.success_desc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button
                  onClick={() => navigate('/surveyor/dashboard')}
                  className="px-8 py-3 border border-slate-200 rounded-lg font-bold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 transition-all"
                >
                  <ArrowLeft className="w-5 h-5" />
                  {t('surveys.portal.editor.back_to_dashboard')}
                </button>
                <a
                  href={generatedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 flex items-center justify-center gap-2 shadow-lg shadow-indigo-200 transition-all transform hover:scale-[1.02]"
                >
                  <Download className="w-5 h-5" />
                  {t('surveys.portal.editor.download_pdf')}
                </a>
              </div>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-8 text-slate-900 tracking-tight">{t('surveys.portal.editor.creator_title')}</h2>
              
              <div className="mb-8">
                <label className="block text-sm font-bold text-slate-700 mb-2">{t('surveys.portal.editor.assessment_label')}</label>
                <p className="text-xs text-slate-400 mb-3 italic">{t('surveys.portal.editor.assessment_desc')}</p>
                <textarea
                  rows={8}
                  className="w-full p-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-sans text-slate-800 leading-relaxed outline-none transition-all shadow-sm"
                  value={assessmentText}
                  onChange={(e) => setAssessmentText(e.target.value)}
                />
              </div>

              <div className="bg-slate-50 p-4 rounded-xl mb-10 border border-slate-100 shadow-inner">
                 <p className="text-xs text-slate-500 font-medium flex items-start gap-2">
                   <span className="text-indigo-500 font-bold">INFO:</span>
                   {t('surveys.portal.editor.auto_info_note')}
                 </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button 
                  onClick={() => navigate(-1)}
                  className="px-6 py-2.5 border border-slate-200 rounded-lg font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  disabled={generating}
                >
                  {t('surveys.portal.editor.back_button')}
                </button>
                <button 
                  onClick={handleGenerate}
                  disabled={generating}
                  className="px-8 py-2.5 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-indigo-100 transition-all transform active:scale-95"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('surveys.portal.editor.generating')}
                    </>
                  ) : (
                    t('surveys.portal.editor.generate_button')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
        
      </div>
    </div>
  );
};
